// app/javascript/controllers/minimum_stay_validator_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Validates minimum stay requirements and disables the view details button
 * when requirements are not met
 * 
 * Rule: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
 */
export default class extends Controller {
  static targets = ["startDate", "endDate", "minimumStayWarning", "viewButton"]
  
  static values = {
    baseMinimumStay: Number,
    weekendMinimumStay: Number,
    holidayMinimumStay: Number,
    rentalId: String
  }
  
  connect() {
    // Find date inputs from the page if targets aren't explicitly set
    this.findDateInputs()
    
    // Set up event listeners for date changes from the datepicker controller
    this.dateChangedHandler = this.handleDateChanged.bind(this)
    document.addEventListener('flowbite-datepicker:dateChanged', this.dateChangedHandler)
    
    // Initial validation
    this.validateDates()
  }
  
  disconnect() {
    // Clean up event listeners
    document.removeEventListener('flowbite-datepicker:dateChanged', this.dateChangedHandler)
  }
  
  findDateInputs() {
    // Try to find date inputs if they're not explicitly set as targets
    if (!this.hasStartDateTarget) {
      this.startDateElement = document.querySelector('input[name="start_date"]')
    } else {
      this.startDateElement = this.startDateTarget
    }
    
    if (!this.hasEndDateTarget) {
      this.endDateElement = document.querySelector('input[name="end_date"]')
    } else {
      this.endDateElement = this.endDateTarget
    }
  }
  
  async handleStartDateChange(event) {
    // When start date changes, fetch applicable pricing rules
    const startDate = this.parseDate(event.target.value)
    
    if (!startDate) {
      this.enableViewButton(true)
      return
    }
    
    // Format date as YYYY-MM-DD for API
    const formattedDate = this.formatDateForApi(startDate)
    
    try {
      // Get rental ID from data attribute or from URL
      let rentalId = this.rentalIdValue
      
      if (!rentalId) {
        // Try to extract from URL
        const urlPath = window.location.pathname
        const matches = urlPath.match(/\/rentals\/([^\/]+)/)
        if (matches && matches[1]) {
          rentalId = matches[1]
        }
      }
      
      if (!rentalId) {
        console.error("Could not determine rental ID for pricing rules")
        this.validateDates() // Fall back to basic validation
        return
      }
      
      // Fetch pricing rules for this date
      const response = await fetch(`/rentals/${rentalId}/pricing_rules?date=${formattedDate}`)
      
      if (!response.ok) {
        throw new Error(`Error fetching pricing rules: ${response.statusText}`)
      }
      
      const data = await response.json()
      
      // Store the pricing rules data
      this.pricingRulesData = data
      
      // Show appropriate messages based on the pricing rules
      this.updateMinimumStayMessage(data, startDate)
      
      // Validate dates with the new pricing rules data
      this.validateDates()
    } catch (error) {
      console.error("Error fetching pricing rules:", error)
      this.validateDates() // Fall back to basic validation
    }
  }
  
  updateMinimumStayMessage(data, startDate) {
    if (!this.hasMinimumStayWarningTarget) return
    
    const dayOfWeek = startDate.getDay()
    const isFriday = dayOfWeek === 5
    const isSaturday = dayOfWeek === 6
    
    // Check for weekend rules
    if ((isFriday || isSaturday) && data.weekend_rules.length > 0) {
      const weekendRule = data.weekend_rules[0]
      const minimumStay = weekendRule.minimum_stay
      const pattern = weekendRule.booking_pattern
      
      // Check if the pattern applies to this day
      let shouldShowMessage = false
      
      switch (pattern) {
        case 'friday_saturday':
          // Only show for Friday check-ins
          shouldShowMessage = isFriday
          break
        case 'saturday_sunday':
          // Only show for Saturday check-ins
          shouldShowMessage = isSaturday
          break
        case 'thursday_friday_saturday':
          // Only show for Thursday check-ins (handled elsewhere)
          shouldShowMessage = false
          break
        case 'friday_saturday_sunday':
          // Only show for Friday check-ins
          shouldShowMessage = isFriday
          break
        case 'any':
          // Show for both Friday and Saturday
          shouldShowMessage = true
          break
        default:
          // Default to showing the message
          shouldShowMessage = true
      }
      
      if (shouldShowMessage) {
        let message = ''
        switch (pattern) {
          case 'friday_saturday':
            message = `Friday check-ins require a minimum stay of ${minimumStay} nights (Friday-Saturday)`
            break
          case 'saturday_sunday':
            message = `Saturday check-ins require a minimum stay of ${minimumStay} nights (Saturday-Sunday)`
            break
          default:
            message = `Weekend check-in requires a minimum stay of ${minimumStay} nights`
        }
        
        // Add more specific message based on booking pattern
        if (pattern !== 'any' && pattern !== 'friday_saturday' && pattern !== 'saturday_sunday') {
          message += ` (${this.formatBookingPattern(pattern)})`
        }
        
        this.minimumStayWarningTarget.textContent = message
        this.minimumStayWarningTarget.classList.remove('hidden')
        this.minimumStayWarningTarget.classList.add('bg-blue-50', 'text-blue-800', 'p-2', 'rounded')
        return
      }
    }
    
    // Check for holiday/event rules
    if (data.holiday_rules.length > 0) {
      const holidayRule = data.holiday_rules[0]
      const minimumStay = holidayRule.minimum_stay
      const pattern = holidayRule.booking_pattern
      
      let message = `${holidayRule.name} requires a minimum stay of ${minimumStay} nights`
      
      // Add more specific message based on booking pattern
      if (pattern !== 'any') {
        message += ` (${this.formatBookingPattern(pattern)})`
      }
      
      this.minimumStayWarningTarget.textContent = message
      this.minimumStayWarningTarget.classList.remove('hidden')
      this.minimumStayWarningTarget.classList.add('bg-blue-50', 'text-blue-800', 'p-2', 'rounded')
      return
    }
    
    // If no special rules apply, hide the message
    this.minimumStayWarningTarget.classList.add('hidden')
  }
  
  formatBookingPattern(pattern) {
    switch (pattern) {
      case 'thursday_friday_saturday':
        return 'Thursday to Sunday'
      case 'friday_saturday_sunday':
        return 'Friday to Monday'
      case 'friday_saturday':
        return 'Friday to Sunday'
      case 'saturday_sunday':
        return 'Saturday to Monday'
      default:
        return 'Any combination of days'
    }
  }
  
  formatDateForApi(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  
  validateDates() {
    // Get start and end dates
    const startDate = this.parseDate(this.startDateElement?.value)
    const endDate = this.parseDate(this.endDateElement?.value)
    
    if (!startDate || !endDate) {
      // If either date is missing, don't show any warning
      this.enableViewButton(true)
      return
    }
    
    // Calculate number of nights
    const nights = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))
    
    // Always ensure end date is after start date
    if (endDate <= startDate) {
      this.disableViewButton("Please select a check-out date that is after your check-in date")
      return
    }
    
    // Ensure minimum one night stay
    if (nights < 1) {
      this.disableViewButton("Your stay must be at least one night")
      return
    }
    
    // Get the day of week (0 = Sunday, 6 = Saturday)
    const startDay = startDate.getDay()
    const isWeekend = startDay === 5 || startDay === 6 // Friday or Saturday
    
    // If we have pricing rules data from the API, use that
    if (this.pricingRulesData) {
      const data = this.pricingRulesData
      // Check for weekend rules first
      if (isWeekend && data.weekend_rules && data.weekend_rules.length > 0) {
        const weekendRule = data.weekend_rules[0]
        // Check if the booking pattern applies to this day of week
        const pattern = weekendRule.booking_pattern
        const isFriday = startDay === 5
        const isSaturday = startDay === 6
        
        let shouldApplyRule = false
        
        // Apply rule based on booking pattern and check-in day
        switch (pattern) {
          case 'friday_saturday':
            // Only apply to Friday check-ins
            shouldApplyRule = isFriday
            break
          case 'saturday_sunday':
            // Only apply to Saturday check-ins
            shouldApplyRule = isSaturday
            break
          case 'thursday_friday_saturday':
            // Only apply to Thursday check-ins (handled elsewhere)
            shouldApplyRule = false
            break
          case 'friday_saturday_sunday':
            // Only apply to Friday check-ins
            shouldApplyRule = isFriday
            break
          case 'any':
            // Apply to both Friday and Saturday
            shouldApplyRule = true
            break
          default:
            // Default to applying the rule
            shouldApplyRule = true
        }
        
        if (shouldApplyRule && nights < weekendRule.minimum_stay) {
          // Create a more specific message based on the pattern
          let message = ''
          switch (pattern) {
            case 'friday_saturday':
              message = `Friday check-ins require a ${weekendRule.minimum_stay}-night minimum stay (Friday-Saturday)`
              break
            case 'saturday_sunday':
              message = `Saturday check-ins require a ${weekendRule.minimum_stay}-night minimum stay (Saturday-Sunday)`
              break
            default:
              message = `Weekend check-ins require a ${weekendRule.minimum_stay}-night minimum stay`
          }
          
          this.disableViewButton(message)
          return
        }
      }
      
      // Then check holiday rules
      if (data.holiday_rules.length > 0) {
        const holidayRule = data.holiday_rules[0]
        if (nights < holidayRule.minimum_stay) {
          this.disableViewButton(`${holidayRule.name} requires a ${holidayRule.minimum_stay}-night minimum stay`)
          return
        }
      }
    } else {
      // Fall back to data attributes
      let requiredStay = this.baseMinimumStayValue || 1
      let stayType = 'standard'
      
      // Check weekend rules first for Friday/Saturday check-ins
      if (isWeekend) {
        // Get the specific day
        const isFriday = startDay === 5
        const isSaturday = startDay === 6
        
        // Default weekend pattern (if not specified) is 'friday_saturday'
        // which only applies to Friday check-ins
        const defaultPattern = 'friday_saturday'
        
        // Check if the pattern applies to this day
        let shouldApplyRule = false
        
        // For the fallback logic, we assume 'friday_saturday' pattern
        // unless overridden by a data attribute
        if (isFriday) {
          // Friday check-ins always get the weekend minimum stay
          requiredStay = this.weekendMinimumStayValue
          stayType = 'weekend'
          shouldApplyRule = true
        } else if (isSaturday) {
          // Saturday check-ins only get weekend minimum stay if pattern is 'any' or 'saturday_sunday'
          // Since we don't have pattern info here, we'll assume it's 'friday_saturday' and NOT apply
          shouldApplyRule = false
        }
        
        // Validate against the required stay
        if (shouldApplyRule && nights < requiredStay) {
          let message = `Friday check-ins require a ${requiredStay}-night minimum stay (Friday-Saturday)`
          this.disableViewButton(message)
          return
        }
      }
      
      // Check holiday minimum stay
      if (this.holidayMinimumStayValue && this.holidayMinimumStayValue > requiredStay) {
        requiredStay = this.holidayMinimumStayValue
        stayType = "holiday"
      }
      
      // Validate against the required stay
      if (nights < requiredStay) {
        let message = ''
        switch (stayType) {
          case 'weekend':
            message = `Weekend check-ins (Friday/Saturday) require a ${requiredStay}-night minimum stay`
            break
          default:
            message = `Please select at least ${requiredStay} nights for your stay`
        }
        this.disableViewButton(message)
        return
      }
    }
    
    // All validations passed
    this.enableViewButton(true)
  }
  
  getMinimumStayForDate(date) {
    // Default to base minimum stay
    let minimumStay = this.baseMinimumStayValue || 1
    let ruleType = "standard"
    
    // Check if it's a weekend (Friday or Saturday)
    const dayOfWeek = date.getDay()
    const isWeekend = dayOfWeek === 5 || dayOfWeek === 6 // Friday or Saturday
    
    if (isWeekend) {
      // Use weekend minimum stay if it's greater than base minimum
      const weekendStay = this.weekendMinimumStayValue || 2
      if (weekendStay > minimumStay) {
        minimumStay = weekendStay
        ruleType = "weekend"
        
      }
    }
    
    // Check holiday minimum stay
    if (this.holidayMinimumStayValue && this.holidayMinimumStayValue > minimumStay) {
      minimumStay = this.holidayMinimumStayValue
      ruleType = "holiday"
    }
    
    return {
      minimumStay,
      ruleType,
      isWeekend
    }
  }
  
  calculateNights(startDate, endDate) {
    const diffTime = endDate.getTime() - startDate.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }
  
  parseDate(dateString) {
    if (!dateString) return null
    
    // Try to parse the date in MM/DD/YYYY format
    if (dateString.includes('/')) {
      const parts = dateString.split('/')
      if (parts.length !== 3) return null
      
      const month = parseInt(parts[0], 10) - 1 // JS months are 0-indexed
      const day = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)
      
      return new Date(year, month, day)
    }
    
    // Try to parse the date in YYYY-MM-DD format
    if (dateString.includes('-')) {
      const parts = dateString.split('-')
      if (parts.length !== 3) return null
      
      const year = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1 // JS months are 0-indexed
      const day = parseInt(parts[2], 10)
      
      return new Date(year, month, day)
    }
    
    // If all else fails, try native Date parsing
    return new Date(dateString)
  }
  
  handleDateChanged(event) {
    const { type, date, nights } = event.detail ?? {}
    // Always validate dates when either date changes
    this.validateDates()
  }
  
  disableViewButton(message = null) {
    if (this.hasViewButtonTarget) {
      this.viewButtonTarget.disabled = true
      this.viewButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
    
    if (message && this.hasMinimumStayWarningTarget) {
      // Create a more eye-catching warning message with icon and better styling
      this.minimumStayWarningTarget.innerHTML = `
        <div class="rounded-md bg-orange-50 p-4 border-l-4 border-orange-500 shadow-sm">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-orange-800">Minimum Stay Requirement</h3>
              <div class="mt-2 text-sm text-orange-700">
                <p class="font-semibold">${message}</p>
              </div>
            </div>
          </div>
        </div>
      `
      this.minimumStayWarningTarget.classList.remove('hidden')
    }
  }
  
  enableViewButton(clearWarning = false) {
    // Enable the view button if it exists
    if (this.hasViewButtonTarget) {
      this.viewButtonTarget.disabled = false
      this.viewButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
    
    // Clear the warning message if requested
    if (clearWarning && this.hasMinimumStayWarningTarget) {
      this.minimumStayWarningTarget.innerHTML = ''
      this.minimumStayWarningTarget.classList.add('hidden')
    }
  }
}
